import React, { useState, useMemo } from 'react';
import { FaChartLine, FaGem, FaRss, FaPlus, FaTrash, FaBrain, FaBolt } from 'react-icons/fa';
import { useWatchlist, addToCollection, deleteFromCollection } from '../hooks/useFirestore';
import { useFinance } from '../context/FinanceContext';
import { detectAnomalies } from '../utils/anomalyDetector';

const IntelligenceFeed = () => {
    const { data: watchlist } = useWatchlist();
    const { stats, transactions, settings } = useFinance();
    const [newSymbol, setNewSymbol] = useState('');

    // Spending insights
    const insights = useMemo(() => {
        const items = [];
        if (stats.income > 0) {
            items.push({ text: `Monthly income: ₹${stats.income.toLocaleString()}, expenses: ₹${stats.expenses.toLocaleString()}`, type: 'info' });
        }
        if (stats.savingsRate > 0) {
            items.push({ text: `Savings rate: ${stats.savingsRate.toFixed(1)}% — ${stats.savingsRate > 30 ? 'Excellent!' : stats.savingsRate > 15 ? 'Good pace' : 'Room for improvement'}`, type: stats.savingsRate > 30 ? 'positive' : 'warning' });
        }
        // Top spending category
        const cats = Object.entries(stats.categoryBreakdown || {}).sort((a, b) => b[1] - a[1]);
        if (cats.length > 0) {
            items.push({ text: `Top spending: ${cats[0][0]} at ₹${cats[0][1].toLocaleString()}`, type: 'info' });
        }
        // Anomalies
        const anomalies = detectAnomalies(transactions || [], settings?.categoryBudgets || {}, stats.dailyBudget);
        anomalies.forEach(a => items.push({ text: a.message, type: 'alert' }));
        return items;
    }, [stats, transactions, settings]);

    const handleAddStock = async () => {
        if (!newSymbol.trim()) return;
        await addToCollection('watchlist', { symbol: newSymbol.trim().toUpperCase(), addedAt: new Date().toISOString() });
        setNewSymbol('');
    };

    const handleRemoveStock = async (id) => { await deleteFromCollection('watchlist', id); };

    return (
        <div className="space-y-8">
            <div>
                <p className="text-[10px] font-semibold text-accent-400 uppercase tracking-[0.2em] mb-1">Insights</p>
                <h1 className="text-2xl font-bold tracking-tight text-frost-900">Intelligence Feed</h1>
                <p className="text-frost-500 mt-0.5 text-sm">Spending insights & tactical intelligence</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    {/* Spending Insights */}
                    <div className="glass-card overflow-hidden">
                        <div className="p-5 border-b border-white/[0.04] flex items-center gap-3">
                            <FaBrain className="text-accent-400" />
                            <h3 className="font-bold tracking-tight text-frost-900">Spending Insights</h3>
                        </div>
                        {insights.length === 0 ? (
                            <EmptyState icon={<FaBolt />} text="No insights yet" sub="Upload transactions to generate insights" />
                        ) : (
                            <div className="divide-y divide-white/[0.04]">
                                {insights.map((item, i) => (
                                    <div key={i} className={`p-4 flex items-start gap-3 ${item.type === 'alert' ? 'bg-gold-500/5' : ''}`}>
                                        <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                                            item.type === 'positive' ? 'bg-mint-400' :
                                            item.type === 'alert' ? 'bg-gold-400' :
                                            item.type === 'warning' ? 'bg-orange-400' : 'bg-accent-400'
                                        }`}></div>
                                        <p className="text-sm text-frost-800 font-medium leading-relaxed">{item.text}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Stock Watchlist */}
                    <div className="glass-card p-5">
                        <div className="flex items-center justify-between mb-5">
                            <div className="flex items-center gap-3"><FaChartLine className="text-mint-400" /><h3 className="font-bold text-frost-900 text-sm">Stock Watchlist</h3></div>
                            <div className="flex items-center gap-2">
                                <input type="text" placeholder="Add symbol (e.g., RELIANCE)" value={newSymbol}
                                    onChange={e => setNewSymbol(e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && handleAddStock()}
                                    className="bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-1.5 text-xs text-frost-900 focus:outline-none focus:ring-2 focus:ring-accent-500/30 w-40" />
                                <button onClick={handleAddStock} className="p-1.5 bg-accent-500/10 text-accent-400 rounded-lg hover:bg-accent-500/20"><FaPlus className="text-[10px]" /></button>
                            </div>
                        </div>
                        {(!watchlist || watchlist.length === 0) ? (
                            <EmptyState icon={<FaChartLine />} text="No stocks tracked" sub="Add stocks to your watchlist" />
                        ) : (
                            <div className="space-y-3">
                                {watchlist.map(s => (
                                    <div key={s.id} className="flex items-center justify-between p-3 bg-white/[0.02] rounded-lg border border-white/[0.04] group">
                                        <div>
                                            <p className="text-sm font-bold text-frost-900">{s.symbol}</p>
                                            <p className="text-[10px] text-frost-400 mt-0.5">NSE</p>
                                        </div>
                                        <button onClick={() => handleRemoveStock(s.id)} className="p-1.5 text-frost-400 hover:text-gold-400 opacity-0 group-hover:opacity-100 transition-all"><FaTrash className="text-[10px]" /></button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* News Feed Placeholder */}
                    <div className="glass-card overflow-hidden">
                        <div className="p-5 border-b border-white/[0.04] flex items-center gap-3">
                            <FaRss className="text-accent-400" />
                            <h3 className="font-bold tracking-tight text-frost-900">News Feed</h3>
                        </div>
                        <EmptyState icon={<FaRss />} text="No news items yet" sub="Connect a news API for real-time updates" />
                    </div>
                </div>

                {/* Commodity & Metals placeholder */}
                <div className="glass-card p-6 relative overflow-hidden h-fit">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-accent-500/5 blur-3xl rounded-full"></div>
                    <div className="flex items-center gap-3 mb-6"><FaGem className="text-gold-400" /><h3 className="font-bold text-frost-900">Commodity & Metals</h3></div>
                    <EmptyState icon={<FaGem />} text="No commodity data" sub="Connect a market data source for live rates" />
                </div>
            </div>
        </div>
    );
};

const EmptyState = ({ icon, text, sub }) => (
    <div className="text-center py-6">
        <div className="text-2xl text-frost-300 mb-2 flex justify-center">{icon}</div>
        <p className="text-sm text-frost-500">{text}</p>
        {sub && <p className="text-xs text-frost-400 mt-0.5">{sub}</p>}
    </div>
);

export default IntelligenceFeed;
